/**
 * Open the system browser at a URL (best-effort).
 *
 * Used to send the user to the authorize page. Failures are swallowed — the
 * caller always prints the URL too, so a headless box can open it elsewhere.
 *
 * @module
 */
import { Effect } from "effect"
import { ChildProcess, ChildProcessSpawner } from "effect/unstable/process"

const opener = (
  platform: string,
): { readonly bin: string; readonly args: (url: string) => ReadonlyArray<string> } => {
  if (platform === "darwin") return { bin: "open", args: (url) => [url] }
  if (platform === "win32") return { bin: "cmd", args: (url) => ["/c", "start", "", url] }
  return { bin: "xdg-open", args: (url) => [url] }
}

export const openBrowser = (
  url: string,
): Effect.Effect<void, never, ChildProcessSpawner.ChildProcessSpawner> =>
  Effect.gen(function* () {
    const spawner = yield* ChildProcessSpawner.ChildProcessSpawner
    const { bin, args } = opener(process.platform)
    const command = ChildProcess.make(bin, args(url), { extendEnv: true })
    yield* Effect.scoped(
      Effect.gen(function* () {
        const handle = yield* spawner.spawn(command)
        yield* handle.exitCode
      }),
    ).pipe(Effect.ignore)
  })
